import styled from '@emotion/styled';
import { rem } from 'polished';
import React from 'react';
import {
  borderRadius,
  borderRadiusScaled,
  ComponentColorMode,
  ComponentColors,
  ComponentWithColorMode,
  getComponentColors,
  lineHeightMap,
  spacing,
  spacingScaled,
  TextElement,
} from '../../../foundations';
import { ComponentSize, ComponentWithSizeMode } from '../../../foundations/modes/size';
import { SpaceBetween } from '../../../utils/SpaceBetween/SpaceBetween';
import { IconCheck, IconClose, IconHelp } from '../../icons';
import { InputValidationType } from '../input.types';

export interface ValidationProps extends ComponentWithSizeMode, ComponentWithColorMode {
  id: string;
  type: InputValidationType;
  message: string | React.JSX.Element;
  icon?: React.ReactNode;
}

const iconMap: Record<ValidationProps['type'], React.ReactNode> = {
  informative: <IconHelp />,
  negative: <IconClose />,
  positive: <IconCheck />,
};

// Negative and positive types will overwrite the colorMode.
const typeToColorModeMap: Record<ValidationProps['type'], ComponentColorMode | undefined> = {
  informative: undefined,
  negative: 'negative',
  positive: 'positive',
};

export const Validation: React.FC<ValidationProps> = ({
  size = 'small',
  colorMode = 'neutral',
  id,
  type = 'informative',
  message,
  icon,
}) => {
  const appliedColorMode = typeToColorModeMap[type] || colorMode;
  const colors = getComponentColors(appliedColorMode);
  const iconToShow = icon || iconMap[type];

  return (
    <StyledValidation
      direction="x"
      alignItems="flex-start"
      spacing={spacing.size2}
      padding={spacing.size4}
      colors={colors}
      size={size}
    >
      {iconToShow}
      <StyledTextElement textStyle="caption" size={size} id={id}>
        {message}
      </StyledTextElement>
    </StyledValidation>
  );
};

const StyledValidation = styled(SpaceBetween)<{ colors: ComponentColors; size: ComponentSize }>`
  color: ${({ colors }) => colors.onSubjacent};
  background: ${({ colors }) => colors.fillSubjacent};
  border-radius: ${({ size }) => rem(borderRadiusScaled(borderRadius.rounded4, size))};

  svg {
    flex-shrink: 0;
    height: ${({ size }) => lineHeightMap['caption'][size]};
    width: ${({ size }) => lineHeightMap['caption'][size]};
  }
`;

const StyledTextElement = styled(TextElement)<{ size: ComponentSize }>`
  padding: ${({ size }) => `0 ${spacingScaled(spacing.size2, size)}`};
  word-break: break-word;
`;
